import { useState, useRef } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import PhoneInputBox from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { sendWhatsAppOtp, verifyWhatsAppOtp } from "../../services/authService";
import { useDispatch, useSelector } from "react-redux";
import { updateUserProfile } from "../../store/actions/auth";
import { toast } from "react-toastify";

const STEP_PHONE = "phone";
const STEP_OTP = "otp";

const WhatsAppLinkModal = ({ show, onHide, onSuccess }) => {
  const dispatch = useDispatch();
  const [step, setStep] = useState(STEP_PHONE);
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const otpRefs = useRef([]);

  const { id } = useSelector((state) => state.auth);

  const resetState = () => {
    setStep(STEP_PHONE);
    setPhone("");
    setPhoneError("");
    setOtp(["", "", "", "", "", ""]);
    setOtpError("");
    setLoading(false);
    setResending(false);
  };

  const handleClose = () => {
    resetState();
    onHide();
  };

  const handleSendOtp = async (isResend = false) => {
    if (!phone || phone.length < 7) {
      setPhoneError("Please enter a valid WhatsApp number.");
      return;
    }
    setPhoneError("");
    isResend ? setResending(true) : setLoading(true);
    const result = await sendWhatsAppOtp(id, `+${phone}`);
    isResend ? setResending(false) : setLoading(false);
    if (result?.success) {
      setStep(STEP_OTP);
      setOtp(["", "", "", "", "", ""]);
      setOtpError("");
    } else {
      setPhoneError(result?.message || "Failed to send OTP. Please try again.");
    }
  };

  const handleOtpChange = (value, index) => {
    if (!/^\d*$/.test(value)) return;
    const updated = [...otp];
    updated[index] = value.slice(-1);
    setOtp(updated);
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyOtp = async () => {
    const otpCode = otp.join("");
    if (otpCode.length < 6) {
      setOtpError("Please enter the 6-digit OTP.");
      return;
    }
    setOtpError("");
    setLoading(true);
    const result = await verifyWhatsAppOtp(id, otpCode);
    setLoading(false);
    if (result?.success) {
      dispatch(updateUserProfile({ is_whatsapp_user: true }));
      toast.success("WhatsApp linked successfully! You'll now receive reminders via WhatsApp.");
      onSuccess?.();
      handleClose();
    } else {
      setOtpError(result?.message || "Invalid OTP. Please try again.");
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="sm">
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="d-flex text-primary text-xxl align-items-center gap-2 fw-bold">
          <img
            src="https://cdn2.iconfinder.com/data/icons/social-messaging-ui-color-shapes-2-free/128/social-whatsapp-circle-1024.png"
            alt="WhatsApp"
            height={24}
          />
          Add To WhatsApp
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-2">
        {step === STEP_PHONE ? (
          <>
            <p className="text-primary mb-3">
              Enter your WhatsApp number to receive reminders.
            </p>
            <Form.Group>
              <PhoneInputBox
                country="us"
                enableSearch
                autoFormat
                placeholder="WhatsApp Number"
                value={phone}
                onChange={(val) => {
                  setPhone(val);
                  setPhoneError("");
                }}
                inputClass={`w-100${phoneError ? " border-danger" : ""}`}
                buttonClass={phoneError ? "border-danger" : ""}
                dropdownClass="border-0"
              />
              {phoneError && (
                <div className="text-danger small mt-1">{phoneError}</div>
              )}
            </Form.Group>
          </>
        ) : (
          <>
            <p className="text-primary mb-1">Enter the 6-digit code sent to</p>
            <p className="fw-semibold mb-3">+{phone}</p>
            <div className="d-flex gap-2 justify-content-center mb-2">
              {otp.map((digit, i) => (
                <Form.Control
                  key={i}
                  ref={(el) => (otpRefs.current[i] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(e.target.value, i)}
                  onKeyDown={(e) => handleOtpKeyDown(e, i)}
                  className={`text-center fw-bold p-2${otpError ? " border-danger" : ""}`}
                  style={{ width: "40px", fontSize: "1.2rem" }}
                />
              ))}
            </div>
            {otpError && (
              <div className="text-danger small text-center mb-1">
                {otpError}
              </div>
            )}
            <div className="text-center">
              <Button
                variant="link"
                size="md"
                className="p-0 text-underline text-primary"
                onClick={() => handleSendOtp(true)}
                disabled={resending}
              >
                {resending ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    Resending...
                  </>
                ) : (
                  "Resend OTP"
                )}
              </Button>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        {step === STEP_PHONE ? (
          <button
            className="w-100 btn btn-primary text-white"
            onClick={() => handleSendOtp(false)}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Sending...
              </>
            ) : (
              "Send OTP"
            )}
          </button>
        ) : (
          <button
            className="w-100 btn btn-primary text-white"
            onClick={handleVerifyOtp}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Verifying...
              </>
            ) : (
              "Submit"
            )}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default WhatsAppLinkModal;
